<template>
  <div>
    <ReportHeaderTab />

    <!-- Page Header -->
    <div class="page-header">
      <div>
        <h5 class="page-title">Chart of Accounts</h5>
        <p class="page-sub">Complete list of all ledger accounts grouped by type</p>
      </div>
      <div class="page-actions">
        <div class="dropdown">
          <button class="btn-outline dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
            <font-awesome-icon icon="download" /> Export
          </button>
          <ul class="dropdown-menu dropdown-menu-end">
            <li><a class="dropdown-item" href="#" @click.prevent="exportPDF('chart_of_account_report')">
              <font-awesome-icon icon="file-pdf" class="me-2 text-danger" /> PDF
            </a></li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Skeleton -->
    <template v-if="loading">
      <div class="report-wrap">
        <div class="skeleton skeleton--letterhead mb-4"></div>
        <div v-for="g in 4" :key="g" class="mb-4">
          <div class="skeleton skeleton--sechdr mb-2"></div>
          <div class="skeleton skeleton--row mb-2" v-for="n in 4" :key="n"></div>
        </div>
      </div>
    </template>

    <!-- Empty -->
    <div v-else-if="!hasData" class="data-card">
      <div class="state-block">
        <div class="state-icon">
          <font-awesome-icon icon="sitemap" />
        </div>
        <p class="state-title">No accounts found</p>
        <p class="state-sub">No chart of account data is available.</p>
      </div>
    </div>

    <!-- Report -->
    <div v-else class="report-wrap" ref="pdfContent">

      <!-- Letterhead -->
      <div class="report-letterhead">
        <div class="letterhead-left">
          <div class="letterhead-icon">
            <font-awesome-icon icon="sitemap" />
          </div>
          <div>
            <h2 class="letterhead-title">Chart of Accounts</h2>
            <p class="letterhead-sub">As at {{ formatDate(today) }}</p>
          </div>
        </div>
        <div class="letterhead-right">
          <!-- Type summary counts -->
          <div class="type-counts">
            <div v-for="(accounts, type) in details" :key="type" class="type-count-chip" :class="'chip--' + type">
              <span class="type-count-label">{{ formatType(type) }}</span>
              <span class="type-count-val">{{ toArray(accounts).length }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Account Groups -->
      <div v-for="(accounts, type) in details" :key="type" class="account-group mb-4">
        <div class="group-hdr" :class="'group-hdr--' + type">
          <div class="group-hdr-icon">
            <font-awesome-icon :icon="typeIcon(type)" />
          </div>
          <span class="group-hdr-title">{{ formatType(type) }}</span>
          <span class="group-hdr-count">{{ toArray(accounts).length }} account{{ toArray(accounts).length !== 1 ? 's' : '' }}</span>
        </div>
        <table class="rep-table">
          <thead>
            <tr>
              <th style="width:120px">Code</th>
              <th>Account Name</th>
              <th style="width:140px">Type</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="account in toArray(accounts)" :key="account.id">
              <td><span class="acc-code">{{ account.code }}</span></td>
              <td class="acc-name">{{ account.name }}</td>
              <td>
                <span class="type-badge" :class="'type--' + account.type">
                  {{ formatType(account.type) }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="rep-footer">Generated by Mapiibs Solution</div>

    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';
import { reusableMixin } from '../../../../lib/reusableMixin';
import { useStore } from 'vuex';
import ReportHeaderTab from './components/reportheadertab.vue';
import moment from 'moment';

export default {
  components: { ReportHeaderTab },
  mixins: [reusableMixin],

  data() {
    return {
      details:      {},
      loading:      false,
      crudLoading:  false,
      query:        null,
      today:        new Date(),

      // Exact original tableData
      tableData: { page: 1,
        today:  new Date(),
        draw:   0, length: 10, search: '', column: 0, dir: 'desc', id: '',
      },

      toast: useToast(),
      store: useStore(),
    };
  },

  computed: {
    permission() { return this.store.state.getCurrentUserPermission; },
    hasData()    { return Object.keys(this.details).length > 0; },
  },

  watch: { query() { this.fetchData(); } },
  created() { this.fetchData(); },

  methods: {
    // Exact original fetchData
    fetchData(url = '/accounting/report/chart-of-account') {
      this.loading     = true;
      this.crudLoading = true;
      this.tableData.query = this.query;
      if (this.crudLoading && this.query) { this.crudLoading = false; }
      this.tableData.draw++;
      axios.get(url, { params: this.tableData })
        .then(response => {
          this.loading = false; this.crudLoading = false;
          if (!response.data.status == 200) return;
          this.details = response.data.data;
        })
        .catch(error => {
          this.loading = false; this.crudLoading = false;
          if ([400, 401, 403, 500].includes(error.response?.status)) {
            this.toast.error(error.response.data.message);
          }
        });
    },

    // Convert object or array to array safely
    toArray(val) {
      return Array.isArray(val) ? val : Object.values(val ?? {});
    },

    formatDate(date) { return moment(date).format('DD/MM/YYYY'); },

    formatType(value) {
      return value
        ? value.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
        : '';
    },

    typeIcon(type) {
      const map = {
        asset:     'university',
        liability: 'hand-holding-usd',
        equity:    'chart-pie',
        income:    'arrow-up',
        expense:   'arrow-down',
      };
      return map[type] ?? 'layer-group';
    },
  },
};
</script>

<style scoped>
.page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:14px; margin-bottom:20px; }
.page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub     { font-size:13px; color:#7a90b0; margin:0; }
.page-actions { display:flex; align-items:center; gap:8px; }

.btn-outline { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1px solid #dde5f0; font-size:13px; font-weight:500; padding:8px 14px; border-radius:8px; cursor:pointer; transition:background .15s; }
.btn-outline:hover { background:#f4f7fb; }
.btn-outline::after { display:none !important; }
.me-2 { margin-right:8px; }

/* ── Skeleton ── */
.skeleton     { border-radius:8px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; }
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
.skeleton--letterhead { height:90px; }
.skeleton--sechdr     { height:42px; border-radius:10px; }
.skeleton--row        { height:18px; }
.mb-2 { margin-bottom:8px; } .mb-4 { margin-bottom:20px; }

/* ── Empty ── */
.data-card  { background:#fff; border:1px solid #e4eaf2; border-radius:12px; }
.state-block { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:64px 24px; text-align:center; }
.state-icon  { width:60px; height:60px; border-radius:16px; background:rgba(0,74,173,.1); color:#004AAD; display:flex; align-items:center; justify-content:center; font-size:24px; margin-bottom:16px; }
.state-title { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
.state-sub   { font-size:13px; color:#7a90b0; margin:0; }

/* ── Report Wrap ── */
.report-wrap { background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:32px; }

/* ── Letterhead ── */
.report-letterhead { display:flex; align-items:flex-start; justify-content:space-between; gap:20px; padding-bottom:24px; border-bottom:2px solid #e4eaf2; margin-bottom:28px; flex-wrap:wrap; }
.letterhead-left   { display:flex; align-items:center; gap:16px; }
.letterhead-icon   { width:52px; height:52px; border-radius:14px; background:rgba(0,74,173,.1); color:#004AAD; display:flex; align-items:center; justify-content:center; font-size:22px; flex-shrink:0; }
.letterhead-title  { font-size:22px; font-weight:800; color:#1a2b45; margin:0 0 3px; }
.letterhead-sub    { font-size:13px; color:#7a90b0; margin:0; }
.letterhead-right  { display:flex; align-items:center; }

/* ── Type Count Chips ── */
.type-counts     { display:flex; align-items:center; flex-wrap:wrap; gap:8px; }
.type-count-chip { display:flex; flex-direction:column; align-items:center; border-radius:10px; padding:8px 14px; border:1px solid transparent; min-width:72px; }
.type-count-label { font-size:11px; font-weight:600; text-transform:uppercase; letter-spacing:.04em; }
.type-count-val   { font-size:18px; font-weight:800; margin-top:2px; }

.chip--asset     { background:rgba(0,74,173,.08);   border-color:#c5d6f5;  color:#004AAD;  }
.chip--liability { background:rgba(224,53,53,.07);  border-color:#fac5c5;  color:#e03535;  }
.chip--equity    { background:rgba(124,58,237,.07); border-color:#c4b5fd;  color:#7c3aed;  }
.chip--income    { background:rgba(22,163,74,.07);  border-color:#bbf7d0;  color:#16a34a;  }
.chip--expense   { background:rgba(224,140,0,.07);  border-color:#fed7aa;  color:#e08c00;  }

/* ── Account Group ── */
.account-group   { border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
.account-group.mb-4 { margin-bottom:20px; }

.group-hdr       { display:flex; align-items:center; gap:12px; padding:13px 16px; font-size:14px; font-weight:700; }
.group-hdr--asset     { background:rgba(0,74,173,.07);   color:#004AAD; }
.group-hdr--liability { background:rgba(224,53,53,.07);  color:#e03535; }
.group-hdr--equity    { background:rgba(124,58,237,.07); color:#7c3aed; }
.group-hdr--income    { background:rgba(22,163,74,.07);  color:#16a34a; }
.group-hdr--expense   { background:rgba(224,140,0,.07);  color:#e08c00; }

.group-hdr-icon  { width:32px; height:32px; border-radius:8px; display:flex; align-items:center; justify-content:center; font-size:14px; background:rgba(255,255,255,.5); flex-shrink:0; }
.group-hdr-title { font-size:14px; font-weight:700; }
.group-hdr-count { margin-left:auto; font-size:12px; font-weight:600; opacity:.75; }

/* ── Table ── */
.rep-table { width:100%; border-collapse:collapse; }
.rep-table thead th        { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:10px 14px; border-bottom:1px solid #e4eaf2; background:#f8fafc; }
.rep-table tbody tr        { border-bottom:1px solid #f0f4f8; transition:background .12s; }
.rep-table tbody tr:last-child { border-bottom:none; }
.rep-table tbody tr:hover  { background:#fafbfc; }
.rep-table tbody td        { padding:11px 14px; font-size:13.5px; vertical-align:middle; }

.acc-code { font-family:monospace; font-size:12px; font-weight:700; color:#1a2b45; background:#f4f7fb; border:1px solid #e4eaf2; border-radius:6px; padding:2px 8px; }
.acc-name { font-size:13.5px; color:#1a2b45; font-weight:500; }

/* ── Type Badge ── */
.type-badge      { display:inline-block; border-radius:6px; padding:3px 10px; font-size:12px; font-weight:600; border:1px solid transparent; text-transform:capitalize; }
.type--asset     { background:rgba(0,74,173,.08);   color:#004AAD; border-color:#c5d6f5; }
.type--liability { background:rgba(224,53,53,.07);  color:#e03535; border-color:#fac5c5; }
.type--equity    { background:rgba(124,58,237,.07); color:#7c3aed; border-color:#c4b5fd; }
.type--income    { background:rgba(22,163,74,.07);  color:#16a34a; border-color:#bbf7d0; }
.type--expense   { background:rgba(224,140,0,.07);  color:#e08c00; border-color:#fed7aa; }

/* ── Footer ── */
.rep-footer { text-align:center; margin-top:28px; padding-top:20px; border-top:1px solid #e4eaf2; font-size:12px; color:#aab8cc; }

/* ── Dropdown ── */
.dropdown-menu { border:1px solid #e4eaf2; border-radius:9px; box-shadow:0 8px 24px rgba(0,0,0,.08); padding:6px; min-width:160px; }
.dropdown-item { border-radius:6px; font-size:13px; padding:8px 12px; color:#1a2b45; }
.dropdown-item:hover { background:#f4f7fb; }

/* ── Print ── */
@media print {
  .page-header,.btn-outline,.dropdown { display:none !important; }
  .report-wrap { border:none; padding:0; }
}

/* ── Responsive ── */
@media(max-width:767px) {
  .report-letterhead { flex-direction:column; }
  .report-wrap { padding:16px; }
  .type-counts { gap:6px; }
  .type-count-chip { min-width:60px; padding:6px 10px; }
}
</style>